import { parseHistory, type HistoryEntry, type TrackState } from "../types";

export const ONBOARDING_TRACK = "onboarding";
export const IDENTITY_TRACK = "identity";

/**
 * Per-track stage lists mirroring the backend track registry
 * (apps/backend/amplify/domains/**\/lifecycle/*-track.ts). This is the one
 * piece of backend knowledge duplicated on the client — keep in sync when a
 * track's stage set changes. A Map (not a plain object) keeps dynamic
 * `.get(track)` lookups clear of the security/detect-object-injection rule.
 */
const TRACK_STAGES = new Map<string, string[]>([
  [
    "onboarding",
    [
      "registered",
      "details_required",
      "verifying",
      "input_required",
      "setup_needed",
      "ready",
      "active",
    ],
  ],
  [
    "payouts",
    [
      "idv_required",
      "idv_processing",
      "idv_input_required",
      "bank_required",
      "eligible",
    ],
  ],
  [
    "account_health",
    ["ok", "input_required", "payouts_paused", "charges_blocked"],
  ],
  [
    "identity",
    [
      "not_verified",
      "pending_backfill",
      "verified",
      "duplicate",
      "unrecoverable",
      "failed",
    ],
  ],
  [
    "referral",
    [
      "not_referred",
      "signed_up",
      "qualifying",
      "eligible",
      "rewarded",
      "ineligible",
    ],
  ],
  [
    "activation_bonus",
    ["not_started", "eligible", "paid", "expired", "failed"],
  ],
]);

/** Valid stages for a track, or `[]` for an unknown track. */
export function stagesForTrack(track: string): string[] {
  return TRACK_STAGES.get(track) ?? [];
}

/** Track names present on the lifecycle record. */
export function presentTrackNames(
  tracks: Record<string, TrackState>,
): string[] {
  return Object.keys(tracks);
}

export type ReconciliationStatus = "in_sync" | "drift" | "no_track";

export interface StageReconciliation {
  recordValue: string;
  mirrorValue: string;
  status: ReconciliationStatus;
}

/**
 * Compares the onboarding track's stored stage (`record`) against the
 * denormalized `BusinessProfile.onboardingStage` mirror. `no_track` means the
 * onboarding track is absent (record defaults to "registered" for display);
 * `drift` means the track is set but the mirror disagrees or is null.
 */
export function computeStageReconciliation(
  trackStage: string | null | undefined,
  mirrorStage: string | null | undefined,
): StageReconciliation {
  const recordValue = trackStage ?? "registered";
  const mirrorValue = mirrorStage ?? "—";
  let status: ReconciliationStatus;
  if (trackStage == null) {
    status = "no_track";
  } else if (trackStage === mirrorStage) {
    status = "in_sync";
  } else {
    status = "drift";
  }
  return { recordValue, mirrorValue, status };
}

export interface PartitionedTracks {
  onboarding: TrackState | undefined;
  identity: TrackState | undefined;
  others: Array<[string, TrackState]>;
}

/** Splits tracks into the onboarding spine, the IDV axis, and everything else. */
export function partitionTracks(
  tracks: Record<string, TrackState>,
): PartitionedTracks {
  const others: Array<[string, TrackState]> = [];
  for (const [name, state] of Object.entries(tracks)) {
    if (name === ONBOARDING_TRACK || name === IDENTITY_TRACK) continue;
    others.push([name, state]);
  }
  return {
    onboarding: tracks.onboarding,
    identity: tracks.identity,
    others,
  };
}

/**
 * The canonical non-spine, non-IDV lifecycle tracks, always shown in the
 * "other tracks" strip. `onboarding` is the spine and `identity` is the IDV
 * section, so both are excluded here. Mirrors the backend track registry.
 */
export const OTHER_TRACK_NAMES = [
  "account_health",
  "referral",
  "activation_bonus",
] as const;

/**
 * Every canonical other-track paired with its current state, or `undefined`
 * when the merchant has not entered that track. Absent tracks are surfaced as
 * disabled "empty" rows rather than hidden, so the strip always lists the full
 * set. Uses a Map for the lookup so the dynamic key access stays clear of the
 * security/detect-object-injection lint rule.
 */
export function otherTracksForDisplay(
  tracks: Record<string, TrackState>,
): Array<{ name: string; state: TrackState | undefined }> {
  const byName = new Map(Object.entries(tracks));
  return OTHER_TRACK_NAMES.map((name) => ({ name, state: byName.get(name) }));
}

export interface OnboardingRequirements {
  currentlyDue: string[];
  /** Stripe `current_deadline`, epoch seconds. */
  deadline: number | null;
}

/**
 * Extracts outstanding Stripe requirements from the onboarding track's
 * metadata (written by the backend recompute on `account.updated`). Returns
 * null when there is nothing outstanding, so callers can render-gate on it.
 */
export function parseOnboardingRequirements(
  metadata: Record<string, unknown> | undefined,
): OnboardingRequirements | null {
  if (!metadata) return null;
  const currentlyDue = Array.isArray(metadata.currentlyDue)
    ? metadata.currentlyDue.filter(
        (item): item is string => typeof item === "string",
      )
    : [];
  if (currentlyDue.length === 0) return null;
  const deadline =
    typeof metadata.deadline === "number" ? metadata.deadline : null;
  return { currentlyDue, deadline };
}

/** Onboarding-track history entries, most-recent first; `[]` on bad data. */
export function parseOnboardingTimeline(history: unknown): HistoryEntry[] {
  return parseHistory(history)
    .filter((entry) => entry.track === ONBOARDING_TRACK)
    .sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
}
